import type { ChatSession } from '../types';

const PENDING_AUTH_NAVIGATION_STORAGE_KEY = 'realplc_pending_auth_navigation';

export type PendingAuthNavigation = {
  view: 'workspace' | 'admin';
  sessionId: string | null;
};

function getSessionStorage(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }

  return window.sessionStorage;
}

export function readPendingAuthNavigation(): PendingAuthNavigation | null {
  const storage = getSessionStorage();
  const rawValue = storage?.getItem(PENDING_AUTH_NAVIGATION_STORAGE_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue) as Partial<PendingAuthNavigation>;
    if (parsed.view !== 'workspace' && parsed.view !== 'admin') {
      storage?.removeItem(PENDING_AUTH_NAVIGATION_STORAGE_KEY);
      return null;
    }

    return {
      view: parsed.view,
      sessionId: typeof parsed.sessionId === 'string' ? parsed.sessionId : null,
    };
  } catch {
    storage?.removeItem(PENDING_AUTH_NAVIGATION_STORAGE_KEY);
    return null;
  }
}

export function writePendingAuthNavigation(target: PendingAuthNavigation): void {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  storage.setItem(
    PENDING_AUTH_NAVIGATION_STORAGE_KEY,
    JSON.stringify({
      view: target.view,
      sessionId: target.sessionId,
    }),
  );
}

export function clearPendingAuthNavigation(): void {
  getSessionStorage()?.removeItem(PENDING_AUTH_NAVIGATION_STORAGE_KEY);
}

function normalizeSessionId(sessionId?: string | null): string | null {
  if (!sessionId || sessionId === 'null') {
    return null;
  }

  return sessionId;
}

export function resolveRestoredSession(
  sessions: ChatSession[],
  preferredSessionId?: string | null,
  fallbackSessionId?: string | null,
): ChatSession | null {
  const normalizedPreferredId = normalizeSessionId(preferredSessionId);
  if (normalizedPreferredId) {
    const preferredSession = sessions.find((session) => session.id === normalizedPreferredId);
    if (preferredSession) {
      return preferredSession;
    }
  }

  const normalizedFallbackId = normalizeSessionId(fallbackSessionId);
  if (normalizedFallbackId) {
    const fallbackSession = sessions.find((session) => session.id === normalizedFallbackId);
    if (fallbackSession) {
      return fallbackSession;
    }
  }

  return sessions[0] || null;
}
